import { parseISO } from 'date-fns';
import { omit, pickBy } from 'lodash-es';

import { formatDate, removeHtmlTags } from '@utils';
import {
  BOARDS_ENUMS,
  FILTER_OPERATOR_ENUMS,
  FILTER_RULE_ENUMS,
  BOARDS_ID_TO_URLS_MAP,
  FILTER_FIELDS_TYPES,
} from '@constants';

import {
  BOARD_CODE_TO_KEY,
  MY_ISSUES_MAPPER,
  MY_ISSUES_DATE_FORMAT,
  MY_ISSUES_FILTER_FIELDS,
} from 'components/myIssues/constants';
import {
  getAllDisplayEmailAddress,
  handleEmailListViewDate,
  labelsMap,
} from 'hooks/communication/utils';
import { computeValueByRuleType } from 'components/common/modals/FiltersModalV2/utils';

const UNKNOWN_BOARD_CODE = (boardName) => `Unknown Board (${boardName})`;

const getBoardSlug = (boardCode) => {
  if (!boardCode) return null;

  const boardKey = BOARD_CODE_TO_KEY[boardCode];

  if (!boardKey) return null;

  return BOARDS_ID_TO_URLS_MAP[boardKey];
};

const getPayloadValue = (field, value) => {
  if (field === MY_ISSUES_FILTER_FIELDS.status) {
    return value.split(',').map((item) => Number(item));
  }

  if (field === MY_ISSUES_FILTER_FIELDS.board) {
    return value.split(',');
  }

  if (field === 'customFields.pipedriveId') {
    return Number.isNaN(Number(value)) ? value : parseInt(value, 10);
  }
  return Number.isNaN(Number(value)) ? value.split(',') : parseInt(value, 10);
};

const handleMyIssuesData = (data, statusMapper, teams) => {
  if (!data?.length) return [];
  const boardKeys = Object.keys(BOARDS_ENUMS);

  return data.map((item) => {
    const board = boardKeys.find(
      (key) => BOARDS_ENUMS[key].board === item?.board
    );

    let boardName = '-';
    if (board) {
      boardName = `${BOARDS_ENUMS[board].text} (${item?.board})`;
    } else if (item?.board) {
      boardName = UNKNOWN_BOARD_CODE(item?.board);
    }

    let statusName =
      statusMapper?.find((statusMap) => statusMap?.id === item?.status)?.name ||
      '-';

    /*
    If status is 75 (Completed) or 'FULFILLED', show it as Done.
    It's for consistency per request in INS-4275
    */
    if (item?.status === 75 || item?.status === 'FULFILLED') {
      statusName = 'Done';
    }

    return {
      boardSlug: getBoardSlug(item?.board),
      [MY_ISSUES_MAPPER.id.key]: item?.ticketId,
      [MY_ISSUES_MAPPER.board.key]: boardName,
      [MY_ISSUES_MAPPER.ticketId.key]: item?.ticketId || '-',
      [MY_ISSUES_MAPPER.status.key]: statusName,
      [MY_ISSUES_MAPPER.reason.key]: item?.reason || '-',
      [MY_ISSUES_MAPPER.skuId.key]: item?.skuId || '-',
      [MY_ISSUES_MAPPER.carId.key]: item?.carId || '-',
      [MY_ISSUES_MAPPER.carInternalId.key]: item?.carInternalId || '-',
      [MY_ISSUES_MAPPER.userId.key]: item?.userId || '-',
      [MY_ISSUES_MAPPER.pipedriveId.key]: item?.pipedriveId || '-',
      [MY_ISSUES_MAPPER.pipedriveUrl.key]: item?.pipedriveUrl || '-',
      [MY_ISSUES_MAPPER.subId.key]: item?.subId || '-',
      [MY_ISSUES_MAPPER.bookingId.key]: item?.bookingId || '-',
      [MY_ISSUES_MAPPER.plate.key]: item?.plate || '-',
      [MY_ISSUES_MAPPER.vehicle.key]: item?.vehicle || null,
      [MY_ISSUES_MAPPER.username.key]: item?.username || '-',
      [MY_ISSUES_MAPPER.email.key]: item?.email || '-',
      [MY_ISSUES_MAPPER.team.key]:
        teams?.find((team) => team.value === item?.team)?.label || '-',
      [MY_ISSUES_MAPPER.dueDate.key]: item?.dueDate
        ? formatDate(item?.dueDate, 'dd/MM/yyyy HH:mm')
        : '-',

      [MY_ISSUES_MAPPER.createdAt.key]: item?.createdAt
        ? formatDate(item?.createdAt, MY_ISSUES_DATE_FORMAT)
        : '-',
      [MY_ISSUES_MAPPER.updatedAt.key]: item?.updatedAt
        ? formatDate(item?.updatedAt, MY_ISSUES_DATE_FORMAT)
        : '-',
      [MY_ISSUES_MAPPER.dealer.key]: item?.vehicle?.dealer || '-',
      [MY_ISSUES_MAPPER.financingProvider.key]:
        item?.vehicle?.financingProvider || '-',
    };
  });
};

const handleMyBoardsData = (data, statusMapper) => {
  if (!data?.length) return [];

  const boardsOptions = [];

  const boardStatusesOptions = [];

  data.forEach((item) => {
    boardsOptions.push({
      value: item?.board,
      text: BOARD_CODE_TO_KEY[item?.board] || UNKNOWN_BOARD_CODE(item?.board),
    });

    (item?.statuses || []).forEach((status) => {
      const boardId = item?.board;

      const statusName =
        statusMapper?.find((statusMap) => statusMap?.id === status)?.name ||
        '-';

      boardStatusesOptions.push({
        boardId,
        text: statusName,
        value: status,
      });
    });
  });

  return {
    boardsOptions,
    boardStatusesOptions,
  };
};

// Status constants
const STATUS_DONE = 3;
const STATUS_CUSTOMER_REJECTED = 11;
const STATUS_CANCELLED = 12;
const STATUS_COMPLETED = 75;

export const COMPLETED_STATUSES = [
  STATUS_DONE,
  STATUS_CUSTOMER_REJECTED,
  STATUS_CANCELLED,
  STATUS_COMPLETED,
];

const getMyIssuesPayload = (fields = {}) => {
  const { query, dateFrom, dateTo, tab, completed, dueDate, ...filters } =
    fields;

  const baseRules = [];
  const reasonRules = [];

  const isCompleted = completed === true || completed === 'true';
  if (isCompleted) {
    baseRules.push({
      field: 'status',
      operator: FILTER_OPERATOR_ENUMS['='],
      type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
      value: COMPLETED_STATUSES,
    });
  } else {
    COMPLETED_STATUSES.forEach((status) => {
      baseRules.push({
        field: 'status',
        operator: FILTER_OPERATOR_ENUMS['!='],
        type: FILTER_RULE_ENUMS.ComparableRuleType,
        value: status,
      });
    });
  }

  if (filters) {
    Object.keys(filters).forEach((key) => {
      const field = MY_ISSUES_FILTER_FIELDS[key];
      const mappedField = field || key;

      if (mappedField === MY_ISSUES_FILTER_FIELDS.reason) {
        const reasons = String(filters[key] || '')
          .split(',')
          .filter(Boolean);

        reasons.forEach((reason) => {
          reasonRules.push({
            field: mappedField,
            operator: FILTER_OPERATOR_ENUMS.contains,
            type: FILTER_RULE_ENUMS.CompareStringRuleType,
            value: reason,
          });
        });
        return;
      }

      baseRules.push({
        field: mappedField,
        operator: FILTER_OPERATOR_ENUMS['='],
        type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
        value: getPayloadValue(mappedField, filters[key]),
      });
    });
  }

  if (dateFrom && dateTo) {
    const fromTs = dateFrom ? parseISO(dateFrom).getTime() : null;

    const toTs = dateTo ? parseISO(dateTo).getTime() : null;

    baseRules.push({
      field: 'created_at',
      operator: FILTER_OPERATOR_ENUMS['>='],
      type: FILTER_RULE_ENUMS.CompareDateRuleType,
      value: fromTs,
    });

    baseRules.push({
      field: 'created_at',
      operator: FILTER_OPERATOR_ENUMS['<='],
      type: FILTER_RULE_ENUMS.CompareDateRuleType,
      value: toTs,
    });
  }

  if (dueDate) {
    const startDate = new Date(dueDate).toISOString();
    const endDate = new Date(dueDate).toISOString();

    const dateRule = {
      type: FILTER_RULE_ENUMS.AndRuleType,
      field: null,
      operator: null,
      value: [
        {
          type: FILTER_RULE_ENUMS.CompareDateRuleType,
          field: 'dueDate',
          operator: FILTER_OPERATOR_ENUMS['>='],
          value: computeValueByRuleType(
            FILTER_RULE_ENUMS.CompareDateRuleType,
            FILTER_FIELDS_TYPES.Date,
            startDate
          ),
        },
        {
          type: FILTER_RULE_ENUMS.CompareDateRuleType,
          field: 'dueDate',
          operator: FILTER_OPERATOR_ENUMS['<='],
          value: computeValueByRuleType(
            FILTER_RULE_ENUMS.CompareDateRuleType,
            FILTER_FIELDS_TYPES.Date,
            endDate
          ),
        },
      ],
    };
    baseRules.push(dateRule);
  }

  if (!baseRules.length && !reasonRules.length) {
    return {
      type: FILTER_RULE_ENUMS.EmptyRuleType,
    };
  }

  const andGroup = {
    field: null,
    operator: null,
    type: FILTER_RULE_ENUMS.AndRuleType,
    value: baseRules,
  };

  if (!reasonRules.length) {
    return andGroup;
  }

  return {
    field: null,
    operator: null,
    type: FILTER_RULE_ENUMS.AndRuleType,
    value: [
      andGroup,
      {
        type: FILTER_RULE_ENUMS.OrRuleType,
        value: reasonRules,
      },
    ],
  };
};

const getEmailListPayload = (queryParams = {}, modalFiltersRules = {}) => {
  let rules = [];

  const fields =
    omit(queryParams, ['page', 'pageSize', 'order', 'orderBy', 'tab']) || {}; // This storage contains the values of the outer filters
  const numberFields = [];
  const multiFilterFields = ['team', 'assignee', 'status'];
  const fieldNames = {
    team: 'team',
    status: 'status',
    assignee: 'assigneeEmail',
    labels: 'labels',
    mailboxId: 'mailboxId',
    unread: 'read',
  };
  const booleanFields = ['unread'];

  const getValue = ({ field }) => {
    const fieldValue = queryParams[field];

    if (multiFilterFields.includes(field)) {
      return fieldValue?.split(',');
    }
    if (booleanFields.includes(field)) {
      // eslint-disable-next-line no-unneeded-ternary
      return fieldValue === 'true' ? false : true; // The 'unread' filter is true when 'read' is not 'true'
    }

    return numberFields.includes(field) ? Number(fieldValue) : fieldValue;
  };

  const getType = ({ field }) => {
    if (multiFilterFields.includes(field)) {
      return FILTER_RULE_ENUMS.IsAnyOfRuleType;
    }
    return FILTER_RULE_ENUMS.ComparableRuleType;
  };

  const getField = (field) => {
    return fieldNames[field];
  };

  delete fields.labels;
  delete fields.tab;
  if (fields.unread === 'false') {
    delete fields.unread;
  }
  const newFields = pickBy(fields);
  const keys = Object?.keys?.(newFields);

  if (keys?.length) {
    rules = [
      ...keys.map((field) => {
        return {
          field: getField(field),
          operator: FILTER_OPERATOR_ENUMS['='],
          type: getType({ field }),
          value: getValue({ field }),
        };
      }),
    ];
  }

  if (labelsMap[queryParams.labels]) {
    rules.push({
      field: 'labels',
      operator: FILTER_OPERATOR_ENUMS['='],
      type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
      value: labelsMap[queryParams.labels],
    });
  }

  rules.push(...(modalFiltersRules?.value || []));
  return {
    type: 0,
    field: null,
    operator: null,
    value: rules,
  };
};

export const handleMailBoxData = (data = []) => {
  if (!data?.length) {
    return [];
  }
  const emails = [];
  data?.forEach((item) => {
    const newEmail = {
      ...item,
      createdAt: item?.createdAt ? formatDate(item?.createdAt, 'MMMM dd') : '',
      updatedAt: item?.updatedAt ? formatDate(item?.updatedAt, 'MMMM dd') : '',
      messageDate: handleEmailListViewDate(item?.messageDate),
      assignee: item?.assignee,
      snippet: removeHtmlTags(item?.snippet),
      displayEmailAddress: getAllDisplayEmailAddress(item),
    };
    emails.push(newEmail);
  });
  return emails;
};

export {
  getMyIssuesPayload,
  handleMyIssuesData,
  handleMyBoardsData,
  getEmailListPayload,
};
